import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import Agilite from 'agilite'

// UI Component imports from Ant Design and other libraries
import CustomRow from '../../../reusable-components/CustomRow'
import {
  Button,
  Card,
  Col,
  Empty,
  Modal,
  Row,
  Space,
  Spin,
  theme,
  Typography,
  Divider,
  Tooltip,
  Popconfirm,
  message
} from 'antd'

import CustomButton from '../../../reusable-components/CustomButton'
import ReactQuill from 'react-quill'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChainBroken,
  faCheckCircle,
  faCircleXmark,
  faNoteSticky,
  faInfoCircle,
  faArchive,
  faFilePdf,
  faDownload
} from '@fortawesome/free-solid-svg-icons'

// Custom component imports for various medical functionalities
import DiagnosisCapture from './components/DiagnosisCapture'
import ExaminationProceduresPerformed from '../examination-procedures-performed'

import ClinicalNotesTemplates from './components/ClinicalNotesTemplates'

import CustomLoadingIcon from '../../../reusable-components/CustomLoadingIcon'

// Utility and helper function imports
import { hexToRGBA, handleError, blobToBase64 } from '../../../lib/utils'
import { isReception, isAdmin } from '../../../lib/profile-utils'
import { createDocument } from '../Documents/utils/api'
import { documentsTypes, pdfGenerationEnums } from '../Documents/utils/enums'
import { readClinicalNotes } from '../../examination-utils/examination-lib'

// Style imports
import './notes.css'
import '../../../Examination/examination-utils/style.css'
import ClinicalNotesToolbar from './components/ClinicalNotesToolbar'
import HistoricalNotes from './components/HistoricalNotes'
import { useNoteState } from './hooks/useNoteState'
import { useHistoricalNotes } from './hooks/useHistoricalNotes'
import TranscribeAi from './components/TranscribeAI'
import AIGenerateModal from './components/AIGenerateModal'
import ClinicalTemplates from './components/clinical templates/ClinicalTemplates'
import Imaging from '../Assessment/Imaging'

const { Title } = Typography

// Agilite instance for PDF generation
const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

// Memoized components to prevent unnecessary re-renders
const MemoizedDiagnosisCapture = React.memo(DiagnosisCapture)
const MemoizedReactQuill = React.memo(ReactQuill)

// Quill editor modules and formats
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean']
  ]
}

const quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet']

// PDF Generation Function
const generateNotesPDF = async (
  bookingData,
  noteState,
  allNotes = [],
  authState,
  coreState,
  historicalNotes = [],
  currentHistoricalNoteIndex = 0,
  showHistorical = false
) => {
  try {
    // Prepare notes content
    let notesContent = ''

    console.log('PDF Generation - noteState:', noteState)
    console.log('PDF Generation - allNotes:', allNotes)
    console.log('PDF Generation - bookingData:', bookingData)

    console.log('PDF Generation - showHistorical:', showHistorical)
    console.log('PDF Generation - historicalNotes:', historicalNotes)
    console.log('PDF Generation - currentHistoricalNoteIndex:', currentHistoricalNoteIndex)
    console.log('PDF Generation - historicalNotes type:', typeof historicalNotes)
    console.log('PDF Generation - historicalNotes.length:', historicalNotes?.length)
    console.log('PDF Generation - condition check:', showHistorical && historicalNotes && historicalNotes.length > 0)

    // Check if we're viewing historical notes and have the data
    if (showHistorical && historicalNotes && historicalNotes.length > 0) {
      console.log('ENTERING HISTORICAL NOTES LOGIC')
      const currentHistoricalNote = historicalNotes[currentHistoricalNoteIndex]
      console.log('Current historical note:', currentHistoricalNote)
      console.log('Current historical note content:', currentHistoricalNote?.content)
      console.log('Content length:', currentHistoricalNote?.content?.length)

      if (currentHistoricalNote && currentHistoricalNote.content && currentHistoricalNote.content.trim() !== '') {
        console.log('Using currently viewed historical note')
        const noteDate = dayjs(currentHistoricalNote.createdAt).format('DD/MM/YYYY HH:mm')
        const noteAuthor = currentHistoricalNote.createdByFullName || 'Unknown Doctor'
        notesContent = `<h3>Clinical Note - ${noteDate}</h3>`
        notesContent += `<p><strong>Recorded by:</strong> ${noteAuthor}</p>`
        notesContent += currentHistoricalNote.content
      } else {
        // Current historical note has no content, try to find the first one with content
        console.log('Current historical note has no content, searching for first note with content...')
        const noteWithContent = historicalNotes.find(
          (note) => note.content && note.content.trim() !== '' && note.content !== '<p><br></p>'
        )

        if (noteWithContent) {
          console.log('Found historical note with content:', noteWithContent._id)
          const noteDate = dayjs(noteWithContent.createdAt).format('DD/MM/YYYY HH:mm')
          const noteAuthor = noteWithContent.createdByFullName || 'Unknown Doctor'
          notesContent = `<h3>Clinical Note - ${noteDate}</h3>`
          notesContent += `<p><strong>Recorded by:</strong> ${noteAuthor}</p>`
          notesContent += "<p><em>Note: This is the most recent note with content from this patient's history.</em></p>"
          notesContent += noteWithContent.content
        } else {
          // All historical notes are empty
          console.log('All historical notes are empty')
          const currentNote = currentHistoricalNote
          const noteDate = dayjs(currentNote.createdAt).format('DD/MM/YYYY HH:mm')
          const noteAuthor = currentNote.createdByFullName || 'Unknown Doctor'
          notesContent = `
            <h3>Clinical Note - ${noteDate}</h3>
            <p><strong>Recorded by:</strong> ${noteAuthor}</p>
            <p><strong>Status:</strong> This clinical note entry exists but contains no documented content.</p>
            <p><em>This may indicate the note was created but not completed, or the content was not saved properly.</em></p>
          `
        }
      }
    }
    // Check if there's a current note being worked on
    else if (noteState.currentNote && noteState.currentNote.trim() && noteState.currentNote !== '<p><br></p>') {
      console.log('Using current working note')
      notesContent = `<h3>Current Clinical Note - ${dayjs().format('DD/MM/YYYY HH:mm')}</h3>`
      notesContent += noteState.currentNote
    }
    // If no current note, try to get the specific note being viewed
    else {
      try {
        console.log('No current note, trying to fetch the note being viewed...')

        // Try to get the specific note if we have a noteId
        if (noteState.noteId) {
          const specificNoteQuery = { _id: noteState.noteId }
          const specificNotes = await readClinicalNotes(specificNoteQuery)
          if (specificNotes && specificNotes.length > 0 && specificNotes[0].content) {
            const note = specificNotes[0]
            const noteDate = dayjs(note.createdAt).format('DD/MM/YYYY HH:mm')
            notesContent = `<h3>Clinical Note - ${noteDate}</h3>`
            notesContent += note.content
            console.log('Using specific note by ID')
          }
        }

        // If still no content, get the most recent note for this booking
        if (!notesContent && bookingData?._id) {
          const bookingNotesQuery = { bookingRef: bookingData._id }
          const bookingNotes = await readClinicalNotes(bookingNotesQuery)

          if (bookingNotes && bookingNotes.length > 0) {
            // Get the most recent note with content
            const notesWithContent = bookingNotes
              .filter((note) => note.content && note.content.trim() !== '' && note.content !== '<p><br></p>')
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

            if (notesWithContent.length > 0) {
              const note = notesWithContent[0]
              const noteDate = dayjs(note.createdAt).format('DD/MM/YYYY HH:mm')
              notesContent = `<h3>Clinical Note - ${noteDate}</h3>`
              notesContent += note.content
              console.log('Using most recent note for this booking')
            }
          }
        }
      } catch (error) {
        console.error('Error fetching current note:', error)
      }
    }

    // If still no content, create a detailed summary
    if (!notesContent || notesContent.trim() === '') {
      console.log('No clinical notes found - creating summary')
      notesContent = `
        <h3>Clinical Notes Report</h3>
                <p><strong>Patient:</strong> ${bookingData?.patientRecord?.firstName || ''} ${
        bookingData?.patientRecord?.lastName || ''
      }</p>
        <p><strong>Patient ID:</strong> ${bookingData?.patientRecord?.idNo || 'N/A'}</p>
        <p><strong>Booking Date:</strong> ${
          bookingData?.bookingDate ? dayjs(bookingData.bookingDate).format('DD/MM/YYYY') : 'N/A'
        }</p>
        <p><strong>Medical Professional:</strong> ${authState.agiliteUser.firstName} ${
        authState.agiliteUser.lastName
      }</p>
        <p><strong>Status:</strong> No clinical notes have been documented for this consultation.</p>
        <p><em>This report was generated on ${dayjs().format('DD/MM/YYYY HH:mm')} for record-keeping purposes.</em></p>
      `
      console.log('Using detailed fallback content')
    }

    // Remove HTML tags for plain text version, but keep some formatting
    const cleanContent = notesContent
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()

    // Prepare template data for PDF generation
    const templateData = {
      patientName: `${bookingData?.patientRecord?.firstName || ''} ${
        bookingData?.patientRecord?.lastName || ''
      }`.trim(),
      patientId: bookingData?.patientRecord?.patientId || bookingData?.patientRecord?.idNo || 'N/A',
      dateOfBirth: bookingData?.patientRecord?.dateOfBirth
        ? dayjs(bookingData.patientRecord.dateOfBirth).format('DD/MM/YYYY')
        : 'N/A',
      bookingDate: bookingData?.bookingDate ? dayjs(bookingData.bookingDate).format('DD/MM/YYYY') : 'N/A',
      medProfName: `${authState.agiliteUser.firstName} ${authState.agiliteUser.lastName}`,
      qualification: authState.agiliteUser.extraData?.qualification || 'Medical Professional',
      mpNumber: authState.agiliteUser.extraData?.mpNumber || 'N/A',
      generateDate: dayjs().format('DD/MM/YYYY HH:mm'),
      clinicalNotes: notesContent,
      cleanNotesContent: cleanContent,
      entityLogo: coreState.entity?.entityLogo?.split(',')[1] || '',
      entityIcon: coreState.entity?.entityIcon?.split(',')[1] || '',
      practiceName: coreState.entity?.entityName || 'Medical Practice'
    }

    console.log('Generating clinical notes PDF with data:', templateData)

    // Get clinic details for template
    const clinicDetails = coreState.clinics?.data?.find((clinic) => clinic._id === bookingData?.clinicRef) || {}

    // Update template data with clinic details (following referral letter pattern)
    const enhancedTemplateData = {
      ...templateData,
      // Add clinic details
      practiceName: clinicDetails?.practiceName || coreState.entity?.entityName || 'Medical Practice',
      practiceNo: clinicDetails?.practiceNumber || 'N/A',
      regNo: clinicDetails?.practiceRegNumber || 'N/A',
      clinicAddress: clinicDetails?.address || 'N/A',
      clinicPhone: clinicDetails?.phoneNumber || 'N/A',
      // Use clinical notes content as referral data (referral template expects this field)
      referralData: notesContent,
      // Add medical aid details
      medAidName: bookingData?.patientRecord?.medicalAid?.name || 'N/A',
      medAidPlan: bookingData?.patientRecord?.medicalAid?.plan || 'N/A',
      medAidNumber: bookingData?.patientRecord?.medicalAid?.number || 'N/A',
      dependantNumber: bookingData?.patientRecord?.medicalAid?.dependantNumber || 'N/A',
      phoneNumber: bookingData?.patientRecord?.phoneNumber || 'N/A',
      idNo: bookingData?.patientRecord?.idNo || 'N/A',
      date: dayjs().format('DD MMM YYYY')
    }

    // Generate PDF using Agilite (following referral letter pattern)
    const pdfResponse = await agilite.Connectors.execute(
      pdfGenerationEnums.pdfGeneration.CRAFT_MY_PDF,
      pdfGenerationEnums.pdfGeneration.CREATE_REFERRAL,
      {
        data: JSON.stringify(enhancedTemplateData)
      }
    )

    if (pdfResponse.data && pdfResponse.data.status === 'success') {
      // Convert the PDF URL to base64 (following Documents pattern)
      const response = await fetch(pdfResponse.data.file)
      const blob = await response.blob()
      const base64data = await blobToBase64(blob)

      // Create document record in the system
      const documentName = `Clinical Notes - ${dayjs().format('DD-MM-YYYY HH:mm')}`
      const newDocument = await createDocument(
        base64data,
        {
          name: documentName,
          userRef: bookingData?.userRef,
          bookingRef: bookingData?._id
        },
        documentsTypes.REPORT // Use existing REPORT type for clinical notes
      )

      console.log('Clinical notes PDF document created:', newDocument)

      // Open PDF in new tab
      window.open(pdfResponse.data.file, '_blank', 'noreferrer')

      return newDocument
    } else {
      throw new Error('PDF generation service returned an error')
    }
  } catch (error) {
    console.error('Error generating clinical notes PDF:', error)
    // Create a more user-friendly error message
    if (error.message.includes('template')) {
      throw new Error(
        'PDF template not found. Please contact your administrator to set up the clinical notes PDF template.'
      )
    }
    throw error
  }
}

// Reception/Admin Read-Only Component
const ClinicalNotesReadOnly = ({ bookingData, userRef, setModalOpen, noBooking }) => {
  const { token } = theme.useToken()
  const { noteState } = useNoteState({ bookingData, userRef })
  const [showHistorical, setShowHistorical] = useState(true)
  const [generatingPDF, setGeneratingPDF] = useState(false)

  // Get historical notes data
  const { historicalNotes, currentHistoricalNoteIndex } = useHistoricalNotes({ bookingData, userRef, noteState })

  // Get Redux state
  const authState = useSelector((state) => state.auth)
  const coreState = useSelector((state) => state.core)

  const handleGeneratePDF = async () => {
    setGeneratingPDF(true)
    try {
      // Get all notes for the patient from noteState
      console.log('Generating PDF with noteState:', noteState)
      const document = await generateNotesPDF(
        bookingData,
        noteState,
        noteState.allNotes,
        authState,
        coreState,
        historicalNotes,
        currentHistoricalNoteIndex,
        showHistorical
      )
      // Show success message
      message.success(`Clinical notes PDF generated successfully: ${document.name}`)
    } catch (error) {
      console.error('PDF generation failed:', error)
      message.error(`PDF generation failed: ${error.message}`)
    } finally {
      setGeneratingPDF(false)
    }
  }

  return (
    <CustomRow style={{ marginTop: 12 }}>
      {noBooking && (
        <Col span={24}>
          <FontAwesomeIcon
            size='2x'
            color={token.colorError}
            style={{ float: 'right', cursor: 'pointer' }}
            onClick={() => setModalOpen(false)}
            icon={faCircleXmark}
          />
        </Col>
      )}
      <Col span={24} style={{ minHeight: '100%', position: 'relative' }}>
        <Card
          styles={{
            header: {
              background: hexToRGBA(token.colorPrimary, 0.08),
              borderBottom: `1px solid ${hexToRGBA(token.colorPrimary, 0.1)}`,
              padding: '12px 16px'
            },
            body: { padding: 0 }
          }}
          size='small'
          style={{
            width: '100%',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
            marginBottom: '16px'
          }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Space>
                <FontAwesomeIcon
                  icon={faNoteSticky}
                  style={{
                    color: token.colorPrimary,
                    fontSize: '16px'
                  }}
                />
                <Title level={5} style={{ margin: 0 }}>
                  Clinical Notes (Read Only)
                </Title>
              </Space>
              <Tooltip title='Export notes to PDF'>
                <Button
                  type='text'
                  size='small'
                  loading={generatingPDF}
                  onClick={handleGeneratePDF}
                  disabled={false}
                  icon={
                    <FontAwesomeIcon
                      icon={faFilePdf}
                      style={{
                        color: token.colorPrimary,
                        fontSize: '14px'
                      }}
                    />
                  }
                  style={{
                    border: 'none',
                    boxShadow: 'none',
                    background: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </Tooltip>
            </div>
          }
        >
          <HistoricalNotes
            bookingData={bookingData}
            userRef={userRef}
            noteState={noteState}
            setShowHistorical={setShowHistorical}
          />
        </Card>
      </Col>
    </CustomRow>
  )
}

// Full Clinical Notes Component for Medical Professionals
const ClinicalNotesFullAccess = ({
  bookingData,
  userRef,
  noBooking,
  setModalOpen,
  setBookingData,
  websocketConnection
}) => {
  // Redux state and theme access
  const { token } = theme.useToken()
  const [instructionsModalVisible, setInstructionsModalVisible] = useState(false)
  const [generatingPDF, setGeneratingPDF] = useState(false)

  // Get Redux state
  const authState = useSelector((state) => state.auth)
  const coreState = useSelector((state) => state.core)
  const [showAIGenerateModal, setShowAIGenerateModal] = useState(false)

  const {
    noteState,
    setNoteState,
    showClinicalNotesTemplates,
    setShowClinicalNotesTemplates,
    showHistorical,
    setShowHistorical,
    checkForExistingNote,
    handleCreateNote,
    handleUpdateNote,
    showAiTranscription,
    setShowAiTranscription,
    handleAutoGenerateNotes,
    transcriptionState,
    debouncedNoteCapture
  } = useNoteState({ bookingData, userRef })

  // Get historical notes data for PDF generation
  const { historicalNotes, currentHistoricalNoteIndex } = useHistoricalNotes({ bookingData, userRef, noteState })

  // Automatically create a note if one doesn't exist (but only after checking for existing notes)
  useEffect(() => {
    if (
      !noBooking &&
      !noteState.noteId &&
      !noteState.isCreating &&
      !noteState.isSubmitting &&
      bookingData?._id &&
      !noteState.fetchingError
    ) {
      // Only create note if we've finished checking for existing notes and found none
      if (!noteState.isSubmitting) {
        handleCreateNote()
      }
    }
  }, [
    noteState.noteId,
    noteState.isCreating,
    noteState.isSubmitting,
    noteState.fetchingError,
    noBooking,
    bookingData?._id,
    handleCreateNote
  ])

  const handleShowAIGenerateModal = () => {
    setShowAIGenerateModal(true)
  }

  const handleAIGenerateCancel = () => {
    setShowAIGenerateModal(false)
  }

  const handleAIGenerate = async (options) => {
    await handleAutoGenerateNotes(options)
    setShowAIGenerateModal(false)
  }

  const handleTemplateInsert = (template) => {
    if (noteState.noteId && template.content) {
      // Insert template content into the current note
      const currentContent = noteState.currentNote || ''
      const newContent = currentContent + '\n\n' + template.content
      setNoteState((prev) => ({ ...prev, currentNote: newContent, isUpdating: true }))
      debouncedNoteCapture(noteState.noteId, newContent)
    }
  }

  const handleTemplateGenerate = (template) => {
    // Handle template generation - could save to a templates database
    // For now, just show a success message or handle as needed
  }

  const handleGeneratePDF = async () => {
    setGeneratingPDF(true)
    try {
      // Generate PDF with current and historical notes
      const document = await generateNotesPDF(
        bookingData,
        noteState,
        noteState.allNotes,
        authState,
        coreState,
        historicalNotes,
        currentHistoricalNoteIndex,
        showHistorical
      )
      // Show success message
      message.success(`Clinical notes PDF generated successfully: ${document.name}`)
    } catch (error) {
      console.error('PDF generation failed:', error)
      message.error(`PDF generation failed: ${error.message}`)
    } finally {
      setGeneratingPDF(false)
    }
  }

  const renderCurrentNote = () => (
    <Row>
      <Col span={24}>
        <div className='quill-wrapper' style={{ width: '100%', height: '100%', position: 'relative' }}>
          <CustomLoadingIcon
            loading={noteState.isAutoGenerating || noteState.isSubmitting || noteState.isCreating}
            loadingText={
              noteState.isCreating
                ? 'Creating Note...'
                : noteState.isAutoGenerating
                ? 'AI is Generating Clinical Notes...'
                : 'Loading Notes...'
            }
            content={
              <div style={{ position: 'relative' }}>
                <MemoizedReactQuill
                  key={`editor-${noteState.noteId || 'new'}`}
                  id={`${bookingData?._id}-notes-capture-area`}
                  style={{
                    display: showHistorical ? 'none' : '',
                    width: '100%',
                    height: 'auto',
                    position: 'relative',
                    zIndex: 2
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Start typing your notes here. Your notes save automatically. To create a new note, use the 'Archive & New' button."
                  onChange={(e) => {
                    if (!noteState.isCreating && noteState.noteId) {
                      setNoteState((prev) => ({ ...prev, isUpdating: true, currentNote: e }))
                      debouncedNoteCapture(noteState.noteId, e)
                    }
                  }}
                  value={noteState.currentNote + transcriptionState.tmpTranscript}
                  readOnly={!noteState.noteId || noteState.isCreating}
                />

                {/* Archive Button positioned in bottom right */}
                {noteState.noteId && noteState.currentNote && !showHistorical && (
                  <Tooltip title='Archive current note and create a new blank note' placement='topLeft'>
                    <Popconfirm
                      overlayStyle={{ maxWidth: 450 }}
                      onConfirm={handleCreateNote}
                      okText='I am sure'
                      title='Archive Current Note'
                      description='This will ARCHIVE your current note and create a new blank note. Your current note will be saved and accessible from the historical notes tab, but you will not be able to edit it after archiving. Are you sure you want to proceed?'
                      placement='topLeft'
                    >
                      <Button
                        type='primary'
                        size='small'
                        disabled={
                          noteState.isAutoGenerating ||
                          noteState.isUpdating ||
                          noteState.isCreating ||
                          noteState.isSubmitting
                        }
                        style={{
                          position: 'absolute',
                          bottom: '16px',
                          right: '16px',
                          zIndex: 10,
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 12px',
                          height: 'auto',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        <FontAwesomeIcon icon={faArchive} style={{ fontSize: '12px' }} />
                        Archive
                      </Button>
                    </Popconfirm>
                  </Tooltip>
                )}
              </div>
            }
          />
        </div>
      </Col>
    </Row>
  )

  return (
    <>
      <CustomRow style={{ marginTop: 12 }}>
        {noBooking && (
          <Col span={24}>
            <FontAwesomeIcon
              size='2x'
              color={token.colorError}
              style={{ float: 'right', cursor: 'pointer' }}
              onClick={() => setModalOpen(false)}
              icon={faCircleXmark}
            />
          </Col>
        )}
        {!noBooking && (
          <Col span={24}>
            <MemoizedDiagnosisCapture bookingData={bookingData} setBookingData={setBookingData} />
          </Col>
        )}
        <Col span={24}>
          <Imaging bookingData={bookingData} userRef={userRef} />
        </Col>
        <Col span={24} style={{ minHeight: '100%', position: 'relative' }}>
          <div style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr auto', gap: 12 }}>
            <Row>
              <Col span={24}>
                <Card
                  styles={{
                    header: {
                      background: hexToRGBA(token.colorPrimary, 0.08),
                      borderBottom: `1px solid ${hexToRGBA(token.colorPrimary, 0.1)}`,
                      padding: '12px 16px'
                    },
                    body: !noteState.noteId ? { padding: 0 } : { padding: 0 }
                  }}
                  size='small'
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden',
                    marginBottom: '16px'
                  }}
                  title={
                    <Space>
                      <FontAwesomeIcon
                        icon={faNoteSticky}
                        style={{
                          color: token.colorPrimary,
                          fontSize: '16px'
                        }}
                      />
                      <Title level={5} style={{ margin: 0 }}>
                        Clinical Notes
                      </Title>
                      <ClinicalNotesToolbar
                        handleShowAIGenerateModal={handleShowAIGenerateModal}
                        handleCreateNote={handleCreateNote}
                        setShowAiTranscription={setShowAiTranscription}
                        setShowClinicalNotesTemplates={setShowClinicalNotesTemplates}
                        noBooking={noBooking}
                        setShowHistorical={setShowHistorical}
                        noteState={noteState}
                      />
                      {!showHistorical && (
                        <Tooltip title='View Instructions'>
                          <Button
                            type='text'
                            icon={
                              <FontAwesomeIcon
                                icon={faInfoCircle}
                                style={{
                                  fontSize: '14px',
                                  color: token.colorInfo,
                                  opacity: 0.8
                                }}
                              />
                            }
                            onClick={() => setInstructionsModalVisible(true)}
                            style={{
                              marginLeft: 8,
                              border: 'none',
                              boxShadow: 'none',
                              background: 'transparent',
                              padding: '4px 8px',
                              height: 'auto',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            size='small'
                          />
                        </Tooltip>
                      )}
                    </Space>
                  }
                  extra={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {/* PDF Export Button */}
                      <Tooltip title='Export notes to PDF'>
                        <Button
                          type='text'
                          size='small'
                          loading={generatingPDF}
                          onClick={handleGeneratePDF}
                          disabled={false}
                          icon={
                            <FontAwesomeIcon
                              icon={faFilePdf}
                              style={{
                                color: token.colorPrimary,
                                fontSize: '14px'
                              }}
                            />
                          }
                          style={{
                            border: 'none',
                            boxShadow: 'none',
                            background: 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        />
                      </Tooltip>

                      {/* Save Status */}
                      {!showHistorical && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {noteState.isUpdating || noteState.isSubmitting ? (
                            <Space style={{ alignItems: 'center' }}>
                              <small>{noteState.isSubmitting ? 'LOADING NOTE' : 'SAVING'}</small>
                              <Spin spinning />
                            </Space>
                          ) : (
                            <>
                              <div style={{ alignItems: 'center', display: 'flex', gap: 4 }}>
                                <small>{noteState.fetchingError || noteState.updatingError ? 'ERROR' : 'SAVED'}</small>
                                <FontAwesomeIcon
                                  fontSize={20}
                                  icon={
                                    noteState.fetchingError || noteState.updatingError ? faChainBroken : faCheckCircle
                                  }
                                  color={
                                    noteState.fetchingError || noteState.updatingError
                                      ? token.colorError
                                      : token.colorSuccess
                                  }
                                />
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  }
                >
                  {showHistorical || noBooking ? (
                    <HistoricalNotes
                      bookingData={bookingData}
                      userRef={userRef}
                      noteState={noteState}
                      setShowHistorical={setShowHistorical}
                    />
                  ) : (
                    renderCurrentNote()
                  )}
                </Card>

                {/* Clinical Templates Section */}
                {!noBooking && !showHistorical && (
                  <ClinicalTemplates
                    bookingData={bookingData}
                    userRef={userRef}
                    onTemplateGenerate={handleTemplateGenerate}
                    onTemplateInsert={handleTemplateInsert}
                  />
                )}

                {(noteState.fetchingError || noteState.updatingError) && (
                  <div
                    style={{
                      height: '100%',
                      width: '100%',
                      background: hexToRGBA(token.colorError, 0.2),
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 30
                    }}
                  >
                    <center>
                      <Space direction='vertical'>
                        <h2>Oops something went wrong.</h2>
                        <p>{noteState.fetchingError || noteState.updatingError}</p>
                        <CustomButton
                          size='small'
                          text='Retry'
                          type='success'
                          onClick={() => {
                            if (noteState.fetchingError) {
                              setNoteState((prev) => ({ ...prev, fetchingError: '', isSubmitting: true }))
                              checkForExistingNote()
                            } else {
                              setNoteState((prev) => ({ ...prev, updatingError: '', isUpdating: true }))
                              handleUpdateNote(noteState.noteId, noteState.currentNote)
                            }
                          }}
                        />
                      </Space>
                    </center>
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </Col>
        {!noBooking && (
          <Col span={24} style={{ marginTop: 12 }}>
            <ExaminationProceduresPerformed bookingData={bookingData} userRef={userRef} />
          </Col>
        )}
        <TranscribeAi
          setNoteState={setNoteState}
          setModalOpen={setShowAiTranscription}
          modalOpen={showAiTranscription}
          noteState={noteState}
          debouncedNoteCapture={debouncedNoteCapture}
          userRef={userRef}
          bookingData={bookingData}
          websocketConnection={websocketConnection}
        />
        <Modal
          footer={false}
          maskClosable={false}
          closable={false}
          width={950}
          open={showClinicalNotesTemplates}
          okButtonProps={{ style: { display: 'none' } }}
          cancelButtonProps={{ style: { display: 'none' } }}
          onCancel={() => {
            setShowClinicalNotesTemplates(false)
          }}
          destroyOnClose
        >
          <ClinicalNotesTemplates
            setModalOpen={(value) => setShowClinicalNotesTemplates((prev) => value)}
            setValue={(value) => setNoteState((prev) => ({ ...prev, currentNote: value }))}
            debouncedNoteCapture={debouncedNoteCapture}
            setUpdatingNotes={(value) => setNoteState((prev) => ({ ...prev, isUpdating: value }))}
            noteId={noteState.noteId}
          />
        </Modal>

        {/* AI Generate Modal */}
        <AIGenerateModal
          visible={showAIGenerateModal}
          onCancel={handleAIGenerateCancel}
          onGenerate={handleAIGenerate}
        />
      </CustomRow>

      {/* Instructions Modal */}
      <Modal
        title={
          <Space>
            <FontAwesomeIcon icon={faInfoCircle} style={{ color: token.colorPrimary }} />
            <span>Clinical Notes Instructions</span>
          </Space>
        }
        open={instructionsModalVisible}
        onCancel={() => setInstructionsModalVisible(false)}
        footer={[
          <Button key='close' type='primary' onClick={() => setInstructionsModalVisible(false)}>
            Got it
          </Button>
        ]}
        width={600}
      >
        <div style={{ padding: '16px 0' }}>
          <h3 style={{ marginBottom: '16px' }}>How to Use Clinical Notes</h3>

          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ color: token.colorPrimary, marginBottom: '8px' }}>Getting Started</h4>
            <p>Start typing in the editor area. Your notes are saved automatically as you type.</p>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ color: token.colorPrimary, marginBottom: '8px' }}>Button Functions</h4>
            <ul style={{ paddingLeft: '20px' }}>
              <li>
                <strong>AI Generate:</strong> Use AI to automatically generate clinical notes based on patient data
              </li>
              <li>
                <strong>Voice Transcription:</strong> Convert speech to text for faster note-taking
              </li>
              <li>
                <strong>Templates:</strong> Insert pre-made templates to speed up documentation
              </li>
              <li>
                <strong>Historical:</strong> View all previous notes for this patient
              </li>
              <li>
                <strong>Archive & New:</strong> Save current note and start a fresh one
              </li>
            </ul>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ color: token.colorPrimary, marginBottom: '8px' }}>Tips</h4>
            <ul style={{ paddingLeft: '20px' }}>
              <li>Notes are automatically saved as you type</li>
              <li>Use the Archive button to create separate notes for different visits</li>
              <li>Historical notes cannot be edited once archived</li>
              <li>Templates can help maintain consistency in documentation</li>
            </ul>
          </div>
        </div>
      </Modal>
    </>
  )
}

// Main Component with Role-Based Access
const ClinicalNotes = ({ bookingData, userRef, noBooking, setModalOpen, setBookingData, websocketConnection }) => {
  // Check if user is reception or admin - they get restricted access
  const isRestrictedRole = isReception() || isAdmin()

  if (isRestrictedRole) {
    return (
      <ClinicalNotesReadOnly
        bookingData={bookingData}
        userRef={userRef}
        setModalOpen={setModalOpen}
        noBooking={noBooking}
      />
    )
  }

  // Medical professionals get full access
  return (
    <ClinicalNotesFullAccess
      bookingData={bookingData}
      userRef={userRef}
      noBooking={noBooking}
      setModalOpen={setModalOpen}
      setBookingData={setBookingData}
      websocketConnection={websocketConnection}
    />
  )
}

export default ClinicalNotes
